import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type AdminAddress, type AdminAddressCheck, tenantHandleSchema } from "@ternary/contracts"
import { Button, Card, Field, Input, ToneIcon } from "@ternary/ui"
import { type FormEvent, useEffect, useId, useRef, useState } from "react"
import { Navigate } from "react-router-dom"
import { checkAdminAddress, saveAdminAddress } from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { LiveAddress } from "../components/live-address.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { ViewOnlyNotice } from "../components/view-only.js"
import { adminAddressQuery, SettingsHeader } from "./admin-settings-page.js"

/** How long the typing has to pause before the handle is checked. */
const CHECK_DELAY_MS = 400

// The contract's own format message: an empty handle fails only its format rule.
const FORMAT_MESSAGE =
  tenantHandleSchema.safeParse("").error?.issues[0]?.message ?? "Enter a valid address."

// The current handle needs no message: the field already shows it.
const CHECK_MESSAGES: Record<
  Exclude<AdminAddressCheck["reason"], "current" | undefined>,
  string
> = {
  invalid: FORMAT_MESSAGE,
  reserved: "That address is reserved.",
  taken: "That address is taken.",
}

function redirectDate(value: string) {
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(value))
}

/**
 * What the field says about `draft` once the typing pauses for 400 ms: the format and reserved
 * words from the contract, the current handle, or admin-api's check. Nothing while it is typed.
 */
function useHandleCheck(draft: string, current: string): { error?: string; hint?: string } {
  const [settled, setSettled] = useState(draft)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(draft), CHECK_DELAY_MS)
    return () => clearTimeout(timer)
  }, [draft])
  const parsed = tenantHandleSchema.safeParse(settled)
  const handle = parsed.success ? parsed.data : null
  const check = useQuery({
    enabled: handle !== null && handle !== current,
    queryFn: ({ signal }) => checkAdminAddress(handle ?? "", { signal }),
    queryKey: ["admin-address-check", handle],
  })
  if (settled !== draft) return {}
  if (!parsed.success) return { error: parsed.error.issues[0]?.message ?? FORMAT_MESSAGE }
  if (handle === current) return {}
  if (check.isError) return { hint: "Could not check this address. Saving will check it." }
  if (!check.data) return {}
  if (check.data.available) return { hint: "Available" }
  const { reason = "taken" } = check.data
  return reason === "current" ? {} : { error: CHECK_MESSAGES[reason] }
}

function AddressForm({
  address,
  id,
  onEdit,
  onSave,
  refused,
  saved,
}: {
  address: AdminAddress
  id: string
  /** The handle changed, so a refused save's message or the saved notice no longer applies. */
  onEdit(): void
  /** A valid handle to save, which may be the current one. */
  onSave(handle: string): void
  /** Why admin-api refused the last save, or "". */
  refused: string
  /** The last save went through, so this form was just rebuilt from the saved handle. */
  saved: boolean
}) {
  const input = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState(address.handle)
  // A Save of a handle the contract refuses, until the handle is edited again.
  const [invalid, setInvalid] = useState("")
  const check = useHandleCheck(draft, address.handle)
  // After a save, either way, the focus is back in the field: a refusal is fixed there, and a
  // success rebuilt the form, which would otherwise drop the focus onto the page (and the Save
  // button was disabled while it ran).
  useEffect(() => {
    if (refused || saved) input.current?.focus()
  }, [refused, saved])

  function submit(event: FormEvent) {
    event.preventDefault()
    const parsed = tenantHandleSchema.safeParse(draft)
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? FORMAT_MESSAGE)
      input.current?.focus()
      return
    }
    onSave(parsed.data)
  }

  const error = invalid || refused || check.error
  return (
    <form aria-label="Address" className="admin-stack" id={id} noValidate onSubmit={submit}>
      {/* A live hint: "Available" and the current address are announced, as errors are. */}
      <Field error={error} hint={check.hint} label="Your address" liveHint>
        {/* The base domain after the handle, inside the field's frame, as the address reads. */}
        <Input
          autoCapitalize="none"
          autoComplete="off"
          maxLength={40}
          onChange={(event) => {
            setDraft(event.currentTarget.value)
            setInvalid("")
            onEdit()
          }}
          ref={input}
          spellCheck={false}
          suffix={`.${address.baseDomain}`}
          value={draft}
        />
      </Field>
      <p className="admin-note">
        You can change it 3 times in 30 days. An earlier address keeps redirecting here for 30 days.
      </p>
    </form>
  )
}

/** The save's confirmation, shown as a success status; the page's other notices are neutral. */
const SAVED = "Address saved."

/**
 * Settings → Address (G2): the business's automatic address `<handle>.<base domain>`, with Copy,
 * and the owner's rename of its handle. Without one (no base domain) it goes back to Settings.
 */
export function AdminAddressPage() {
  const formId = useId()
  const listId = useId()
  const queryClient = useQueryClient()
  const query = useQuery(adminAddressQuery)
  const [notice, setNotice] = useState("")
  const save = useMutation({
    mutationFn: saveAdminAddress,
    onMutate() {
      setNotice("")
    },
    onSuccess(next) {
      queryClient.setQueryData(adminAddressQuery.queryKey, next)
      void queryClient.invalidateQueries({ queryKey: ["admin-address-check"] })
      setNotice(SAVED)
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <SettingsHeader active="address" />
        {query.isPending ? (
          <FormLoading fields={1} label="Loading your address…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your address"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }
  const address = query.data
  if (!address) return <Navigate replace to="/dashboard/settings" />

  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active="address" />
      {address.changeable ? null : <ViewOnlyNotice />}
      <Card title="Address">
        <div className="admin-stack">
          <p className="admin-note">Your signup links start with this address.</p>
          <LiveAddress url={address.url} />
          {address.changeable ? (
            // Keyed by the saved handle, so a save starts the field from the new one. Only here, with
            // an address and an account the server lets rename it (owner or admin), is the check
            // ever asked: without an address it answers 404.
            <AddressForm
              address={address}
              id={formId}
              key={address.handle}
              onEdit={() => {
                setNotice("")
                if (save.isError) save.reset()
              }}
              onSave={(handle) => {
                if (handle === address.handle) setNotice("This is already your address.")
                else save.mutate(handle)
              }}
              refused={save.error instanceof Error ? save.error.message : ""}
              saved={save.isSuccess}
            />
          ) : null}
          {address.retired.length > 0 ? (
            <div className="admin-stack">
              <h3 className="admin-heading" id={listId}>
                Earlier addresses
              </h3>
              <ul aria-labelledby={listId} className="admin-retired-addresses">
                {address.retired.map(({ hostname, redirectUntil }) => (
                  <li key={hostname}>
                    {hostname} keeps redirecting until{" "}
                    <time dateTime={redirectUntil}>{redirectDate(redirectUntil)}</time>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Card>
      {address.changeable ? (
        <div className="admin-save-bar">
          <p
            className={notice === SAVED ? "admin-save-message is-success" : "admin-save-message"}
            role="status"
          >
            {notice ? (
              <>
                {notice === SAVED ? <ToneIcon tone="success" /> : null}
                {notice}
              </>
            ) : null}
          </p>
          <Button form={formId} loading={save.isPending} type="submit">
            Save
          </Button>
        </div>
      ) : null}
    </section>
  )
}
